import { Box, Typography, IconButton, CircularProgress, Tooltip, Chip } from "@mui/material";
import { Visibility, Download } from "@mui/icons-material";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";

import { IconImage } from "@/components";
import { formatDateOrTime } from "@/utils";
import { excel_img } from "@/constants";

interface CertificateActionsProps {
  action: (type: "view" | "download" | "delete" | "edit", cert: CertificateSummary) => void;
  actionsLoading: {
    view: string[];
    download: string[];
    delete: string[];
    edit: string[];
  };
}

export const columns = ({
  action,
  actionsLoading,
}: CertificateActionsProps): IColumn<CertificateSummary>[] => [
  {
    name: "File Name",
    key: "fileName",
    align: "left",
    width: "250px",
    sorting: true,
    component: (cert) => (
      <Box display="flex" alignItems="center" gap={1}>
        <IconImage src={excel_img} size="24px" />
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "#030712",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": {
                color: "primary.main",
                textDecoration: "underline",
              },
            }}
            onClick={() => action("view", cert)}
          >
            {cert.fileName}
          </Typography>
          <Typography variant="caption" color="#8E9494">
            {cert.itemCount} {cert.itemCount === 1 ? "item" : "items"}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    name: "Upload Date",
    key: "uploadDate",
    align: "left",
    width: "140px",
    sorting: true,
    component: (cert) => (
      <Box>
        <Typography variant="body2" color="#030712">
          {formatDateOrTime(cert.uploadDate, "date")}
        </Typography>
        <Typography variant="body1" fontSize="0.75rem" color="#8E9494">
          {formatDateOrTime(cert.uploadDate, "time")}
        </Typography>
      </Box>
    ),
  },
  {
    name: "Form Type",
    key: "formType",
    align: "left",
    width: "120px",
    sorting: true,
    component: (cert) => (
      <Chip
        label={cert.formType || "Unknown"}
        size="small"
        color={cert.formType === "USMCA" ? "primary" : "default"}
        variant="outlined"
      />
    ),
  },
  {
    name: "Actions",
    key: "actions",
    align: "center",
    width: "240px",
    component: (cert) => (
      <Box display="flex" gap={1} justifyContent="center">
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => action("view", cert)}
            disabled={actionsLoading.view.includes(cert._id)}
            sx={{
              color: "primary.main",
              "&:hover": {
                backgroundColor: "primary.light",
              },
            }}
          >
            {actionsLoading.view.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Edit Certificate">
          <IconButton
            size="small"
            onClick={() => action("edit", cert)}
            disabled={actionsLoading.edit.includes(cert._id)}
            sx={{
              color: "info.main",
              "&:hover": {
                backgroundColor: "info.light",
              },
            }}
          >
            {actionsLoading.edit.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Download Certificate">
          <IconButton
            size="small"
            onClick={() => action("download", cert)}
            disabled={actionsLoading.download.includes(cert._id)}
            sx={{
              color: "success.main",
              "&:hover": {
                backgroundColor: "success.light",
              },
            }}
          >
            {actionsLoading.download.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <Download fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Certificate">
          <IconButton
            size="small"
            onClick={() => action("delete", cert)}
            disabled={actionsLoading.delete.includes(cert._id)}
            sx={{
              color: "error.main",
              "&:hover": {
                backgroundColor: "error.light",
              },
            }}
          >
            {actionsLoading.delete.includes(cert._id) ? (
              <CircularProgress size={16} />
            ) : (
              <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
];